import type { Range } from '@tiptap/core';

import { CellSelection } from '../../../../../../common/table/TableTypes';
import { parseCellName } from '../../utils/selectionUtils/tableSelectionExcelNameUtils';

type HighlightedCellColor = {
    borderColorHex: string;
    backgroundColor: string;
};

const HIGHLIGHTED_CELL_COLORS: HighlightedCellColor[] = [
    {
        // Blue
        borderColorHex: '16a0e5',
        backgroundColor: 'rgba(22, 160, 229, 0.1)',
    },
    {
        // Orange
        borderColorHex: 'ff8d22',
        backgroundColor: 'rgba(255, 141, 34, 0.1)',
    },
    {
        // Green
        borderColorHex: '4eb85a',
        backgroundColor: 'rgba(78, 184, 90, 0.1)',
    },
    {
        // Purple
        borderColorHex: 'af72ff',
        backgroundColor: 'rgba(175, 114, 255, 0.1)',
    },
    {
        // Pink
        borderColorHex: 'ff6dd4',
        backgroundColor: 'rgba(255, 109, 212, 0.1)',
    },
    {
        // Teal
        borderColorHex: '62dbc8',
        backgroundColor: 'rgba(98, 219, 200, 0.1)',
    },
];

type CellReferenceInfo = {
    cellSelection: CellSelection;
    cellSelectionName: string; // text representation, eg "A1" or "B2:C3"
};

// Tracks a single cell reference within a formula - where in the formula & what cell it's referencing
export type CellReference = CellReferenceInfo & Range & HighlightedCellColor;

const CELL_REFERENCE_TOKEN_REGEX = /[^=(),?*/^%&\-+<>;\s]+/g;

/**
 * Finds all cell references in formula text, preserving the order they appear in the source text.
 */
export const getCellReferences = (inputValue: string): CellReference[] =>
    [...inputValue.matchAll(CELL_REFERENCE_TOKEN_REGEX)].reduce<CellReference[]>((acc, match) => {
        const [cellSelectionName] = match;
        const cellSelection = parseCellName(cellSelectionName);

        if (!cellSelection || match.index === undefined) return acc;

        const index = acc.length;

        acc.push({
            cellSelection,
            cellSelectionName,
            from: match.index,
            to: match.index + cellSelectionName.length,
            ...HIGHLIGHTED_CELL_COLORS[index % HIGHLIGHTED_CELL_COLORS.length],
        });

        return acc;
    }, []);

export const findCellReferenceAtSelection = (
    cellReferences: CellReference[],
    selectionStart: number,
): CellReference | undefined => cellReferences.find(({ from, to }) => selectionStart >= from && selectionStart <= to);

// TODO-TABLES: only used by draft plugins, can be deleted
export const findCellReferenceBeforeSelection = (
    cellReferences: CellReference[],
    text: string,
    selectionStart: number,
): CellReference | undefined => {
    return cellReferences.find(
        ({ cellSelectionName }) =>
            text.slice(selectionStart - cellSelectionName.length, selectionStart) === cellSelectionName,
    );
};
